import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { 
  Users, UserPlus, UserCheck, TrendingUp, TrendingDown,
  RefreshCw, Download, Sparkles, Calendar as CalendarIcon,
  Trophy, Target, AlertTriangle, Clock, Video,
  ArrowRight, Phone, ChevronRight, RotateCcw, Ban, Search, X, Filter, ChevronDown
} from "lucide-react";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Area, AreaChart, Legend,
  ComposedChart
} from "recharts";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, subDays, subMonths } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = { New: "#0D0D5B", Contacted: "#3B82F6", Demoed: "#8B5CF6", Subscribed: "#10B981", Lost: "#EF4444" };
const funnelColors = ["#0D0D5B", "#3B82F6", "#8B5CF6", "#10B981"];
const planColors = { Basic: "#3B82F6", Plus: "#8B5CF6", Pro: "#F59E0B", Unspecified: "#9CA3AF" };
const LOST_REASON_COLORS = ["#EF4444", "#F97316", "#F59E0B", "#D97706", "#84CC16", "#10B981", "#06B6D4", "#3B82F6", "#8B5CF6", "#9CA3AF"];

const tooltipStyle = { borderRadius: '8px', border: '1px solid #E5E7EB', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', fontSize: '12px' };

const planBadgeStyles = {
  Basic: "bg-blue-100 text-blue-700 border-blue-200",
  Plus: "bg-purple-100 text-purple-700 border-purple-200",
  Pro: "bg-amber-100 text-amber-800 border-amber-200",
};

// ==================== ENGARAGE SUBSCRIPTIONS BOX ====================
function EngarageSubscriptionsBox({ items = [], navigate, className = "" }) {
  const shown = items.slice(0, 13);
  const [allOpen, setAllOpen] = useState(false);
  const [allData, setAllData] = useState({ items: [], total: 0, summary: { total: 0, free: 0, downgrades: 0 } });
  const [loadingAll, setLoadingAll] = useState(false);
  const [search, setSearch] = useState("");
  const [filterPlan, setFilterPlan] = useState("");
  const [filterActivity, setFilterActivity] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Debounced fetch
  useEffect(() => {
    if (!allOpen) return;
    const t = setTimeout(async () => {
      setLoadingAll(true);
      try {
        const params = new URLSearchParams();
        if (search.trim()) params.set("search", search.trim());
        if (filterPlan) params.set("plan", filterPlan);
        if (filterActivity) params.set("activity", filterActivity);
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);
        params.set("limit", "200");
        const r = await axios.get(`${API}/analytics/engarage-subscriptions?${params}`);
        setAllData(r.data || { items: [], total: 0 });
      } catch (e) { console.error(e); }
      setLoadingAll(false);
    }, 250);
    return () => clearTimeout(t);
  }, [allOpen, search, filterPlan, filterActivity, dateFrom, dateTo]);

  const clearFilters = () => {
    setSearch(""); setFilterPlan(""); setFilterActivity(""); setDateFrom(""); setDateTo("");
  };

  return (
    <>
    <Card className={`border border-emerald-200/60 rounded-xl bg-white flex flex-col ${className}`} data-testid="engarage-subscriptions-box">
      <CardHeader className="pb-0 pt-4 px-5">
        <CardTitle className="text-sm font-semibold text-emerald-700 flex items-center gap-2">
          <UserCheck className="w-4 h-4" /> ENGARAGE Subscription Activity
          <button
            onClick={() => setAllOpen(true)}
            className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-600 hover:text-emerald-800 font-semibold transition-colors"
            data-testid="engarage-show-all-btn"
          >
            Show all <ChevronRight className="w-3 h-3" />
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-3 pb-3 pt-2 flex-1 min-h-0 flex flex-col">
        {shown.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1 py-8 text-gray-300">
            <UserCheck className="w-8 h-8 mb-2" />
            <p className="text-sm text-gray-400">No subscriptions yet</p>
          </div>
        ) : (
          <div className="space-y-0.5 flex-1 overflow-y-auto pr-1" data-testid="engarage-sub-list">
            {shown.map((s, i) => (
              <button
                key={s.lead_id || i}
                onClick={() => s.lead_id && navigate(`/leads/${s.lead_id}`)}
                className={`w-full flex items-center gap-2 px-2 py-1 rounded-md transition-colors text-left border ${s.is_downgrade ? "hover:bg-red-50/70 border-transparent hover:border-red-100" : "hover:bg-emerald-50/60 border-transparent hover:border-emerald-100"}`}
                data-testid={`engarage-sub-${i}`}
              >
                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.is_downgrade ? "bg-red-500" : "bg-emerald-500"}`} />
                <p className="text-[12px] font-medium text-gray-900 truncate flex-1 leading-tight">{s.workshop}</p>
                {s.is_downgrade && (
                  <span className="px-1.5 py-0 rounded-full text-[9px] font-semibold border bg-red-50 text-red-700 border-red-200 shrink-0" data-testid={`engarage-downgrade-${i}`} title={`Downgraded from ${s.previous_plan}`}>
                    {s.previous_plan} → Free
                  </span>
                )}
                {!s.is_downgrade && s.plan && s.plan !== "—" && (
                  <span className={`px-1.5 py-0 rounded-full text-[9.5px] font-semibold border shrink-0 ${planBadgeStyles[s.plan] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
                    {s.plan}
                  </span>
                )}
                <span className="text-[10px] text-gray-400 shrink-0 tabular-nums">
                  {(() => { try { return format(new Date(s.subscribed_at), "d MMM"); } catch { return ""; } })()}
                </span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>

    {/* Slide-in: All Subscription Activity */}
    <Sheet open={allOpen} onOpenChange={setAllOpen}>
      <SheetContent className="w-[820px] sm:max-w-[820px] overflow-y-auto" data-testid="engarage-all-sheet">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <UserCheck className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <SheetTitle className="text-lg font-semibold text-gray-900">ENGARAGE Subscription Activity</SheetTitle>
              <SheetDescription className="text-sm text-gray-500">All subscriptions, plan changes & downgrades</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-2 mt-5">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3">
            <p className="text-[10px] text-emerald-700 font-semibold uppercase tracking-wide">Total Subscribed</p>
            <p className="text-xl font-bold text-emerald-900 mt-0.5">{allData.summary?.total || 0}</p>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-3">
            <p className="text-[10px] text-gray-600 font-semibold uppercase tracking-wide">On Free Plan</p>
            <p className="text-xl font-bold text-gray-900 mt-0.5">{allData.summary?.free || 0}</p>
          </div>
          <div className="rounded-lg border border-red-200 bg-red-50/50 p-3">
            <p className="text-[10px] text-red-700 font-semibold uppercase tracking-wide">Downgrades</p>
            <p className="text-xl font-bold text-red-700 mt-0.5">{allData.summary?.downgrades || 0}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search workshop, company, email, or workshop ID…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-8 border-gray-300 rounded-lg text-sm h-9"
                data-testid="engarage-all-search"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="engarage-all-clear-search">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            {/* Date range picker — compact, inline with search */}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className={`h-9 px-3 rounded-lg border text-xs font-medium inline-flex items-center gap-1.5 shrink-0 transition-colors ${dateFrom || dateTo ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
                  data-testid="engarage-all-date-trigger"
                >
                  <CalendarIcon className="w-3.5 h-3.5" />
                  {dateFrom && dateTo ? (
                    <span className="tabular-nums">{(() => { try { return `${format(new Date(dateFrom), "d MMM")} – ${format(new Date(dateTo), "d MMM")}`; } catch { return "Date range"; } })()}</span>
                  ) : dateFrom ? (
                    <span>From {(() => { try { return format(new Date(dateFrom), "d MMM"); } catch { return ""; } })()}</span>
                  ) : dateTo ? (
                    <span>Until {(() => { try { return format(new Date(dateTo), "d MMM"); } catch { return ""; } })()}</span>
                  ) : (
                    <span>Date range</span>
                  )}
                  <ChevronDown className="w-3 h-3 opacity-60" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-3" align="end" data-testid="engarage-all-date-popover">
                <div className="space-y-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1.5">Quick presets</p>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { id: "today", label: "Today", from: () => format(new Date(), "yyyy-MM-dd"), to: () => format(new Date(), "yyyy-MM-dd") },
                        { id: "7d", label: "Last 7 days", from: () => format(subDays(new Date(), 6), "yyyy-MM-dd"), to: () => format(new Date(), "yyyy-MM-dd") },
                        { id: "30d", label: "Last 30 days", from: () => format(subDays(new Date(), 29), "yyyy-MM-dd"), to: () => format(new Date(), "yyyy-MM-dd") },
                        { id: "mtd", label: "Month to date", from: () => { const d = new Date(); d.setDate(1); return format(d, "yyyy-MM-dd"); }, to: () => format(new Date(), "yyyy-MM-dd") },
                      ].map(p => (
                        <button
                          key={p.id}
                          onClick={() => { setDateFrom(p.from()); setDateTo(p.to()); }}
                          className="text-[11px] py-1.5 px-2 rounded-md border border-gray-200 text-gray-700 hover:bg-emerald-50 hover:border-emerald-300 transition-colors text-left"
                          data-testid={`engarage-all-preset-${p.id}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="border-t border-gray-100 pt-3 space-y-2">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Custom range</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <label className="text-[10px] text-gray-500 block mb-0.5">From</label>
                        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs border-gray-300 rounded-md" data-testid="engarage-all-date-from" />
                      </div>
                      <div className="flex-1">
                        <label className="text-[10px] text-gray-500 block mb-0.5">To</label>
                        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs border-gray-300 rounded-md" data-testid="engarage-all-date-to" />
                      </div>
                    </div>
                  </div>
                  {(dateFrom || dateTo) && (
                    <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="w-full text-[11px] text-gray-500 hover:text-gray-700 inline-flex items-center justify-center gap-1 py-1" data-testid="engarage-all-date-clear">
                      <RotateCcw className="w-3 h-3" /> Clear date range
                    </button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Filter chips */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center text-[10px] uppercase tracking-wider text-gray-400 font-semibold gap-1"><Filter className="w-3 h-3" /> Plan:</span>
            {["", "Basic", "Plus", "Pro", "Free"].map(p => (
              <button
                key={p || "all"}
                onClick={() => setFilterPlan(p)}
                className={`px-2.5 py-1 rounded-full text-[10.5px] font-semibold border transition-colors ${filterPlan === p ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-600 border-gray-200 hover:border-emerald-300"}`}
                data-testid={`engarage-all-filter-plan-${p || "all"}`}
              >
                {p || "All"}
              </button>
            ))}
            <span className="ml-3 inline-flex items-center text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Activity:</span>
            {[{id:"",label:"All"},{id:"subscription",label:"Subscriptions"},{id:"downgrade",label:"Downgrades"}].map(a => (
              <button
                key={a.id || "all"}
                onClick={() => setFilterActivity(a.id)}
                className={`px-2.5 py-1 rounded-full text-[10.5px] font-semibold border transition-colors ${filterActivity === a.id ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-600 border-gray-200 hover:border-emerald-300"}`}
                data-testid={`engarage-all-filter-activity-${a.id || "all"}`}
              >
                {a.label}
              </button>
            ))}
            {(search || filterPlan || filterActivity || dateFrom || dateTo) && (
              <button onClick={clearFilters} className="ml-auto text-[11px] text-gray-500 hover:text-gray-700 inline-flex items-center gap-1" data-testid="engarage-all-clear-filters">
                <RotateCcw className="w-3 h-3" /> Clear all
              </button>
            )}
          </div>
        </div>

        {/* Result count */}
        <div className="mt-3 text-[11px] text-gray-500 flex items-center justify-between">
          <span>{loadingAll ? "Loading…" : `${allData.items.length} of ${allData.total} matching`}</span>
        </div>

        {/* Activity rows */}
        <div className="mt-2 border border-gray-200 rounded-xl overflow-hidden" data-testid="engarage-all-list">
          {(allData.items || []).length === 0 ? (
            <div className="py-10 text-center text-sm text-gray-400">
              {loadingAll ? "Loading subscriptions…" : "No subscription activity matches your filters."}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {allData.items.map((s, i) => (
                <button
                  key={s.lead_id || i}
                  onClick={() => { setAllOpen(false); s.lead_id && navigate(`/leads/${s.lead_id}`); }}
                  className={`w-full flex items-start gap-3 p-3 text-left transition-colors ${s.is_downgrade ? "hover:bg-red-50/40" : "hover:bg-emerald-50/40"}`}
                  data-testid={`engarage-all-row-${i}`}
                >
                  <div className={`w-2 h-2 rounded-full shrink-0 mt-2 ${s.is_downgrade ? "bg-red-500" : "bg-emerald-500"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-900 truncate">{s.workshop}</p>
                      {s.is_downgrade ? (
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold border bg-red-50 text-red-700 border-red-200">
                          {s.previous_plan} → Free
                        </span>
                      ) : (
                        s.plan && s.plan !== "—" && (
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${planBadgeStyles[s.plan] || "bg-gray-100 text-gray-600 border-gray-200"}`}>
                            {s.plan}
                          </span>
                        )
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px] text-gray-500">
                      {s.email && <span className="truncate">{s.email}</span>}
                      {s.workshop_id && <span className="font-mono text-gray-400">· {s.workshop_id}</span>}
                      {s.assigned_name && <span>· assigned to <span className="font-medium text-gray-700">{s.assigned_name}</span></span>}
                    </div>
                    <p className="mt-1 text-[10.5px] text-gray-400">
                      {(() => { try { return format(new Date(s.subscribed_at), "d MMM yyyy, h:mm a"); } catch { return ""; } })()}
                      {s.subscribed_by && <> · by <span className={s.subscribed_via === "engarage" ? "text-teal-700" : "text-gray-600"}>{s.subscribed_by}</span></>}
                    </p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 shrink-0 mt-1" />
                </button>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
    </>
  );
}

// ==================== ALL-CCO SCHEDULE BOX (admin/marketing) ====================
function AllCcoScheduleBox({ items = [], navigate, teamMembers = [], className = "" }) {
  const [calDate, setCalDate] = useState(new Date());

  // Index events by date for calendar dot indicators
  const eventsByDate = {};
  const todayStr = new Date().toISOString().slice(0, 10);
  (items || []).forEach(ev => {
    try {
      const d = new Date(ev.meeting.start).toDateString();
      if (!eventsByDate[d]) eventsByDate[d] = [];
      const isBreached = ev.event_type === "scheduled_followup" && (ev.meeting?.start || "").slice(0, 10) < todayStr;
      eventsByDate[d].push({ ...ev, breached: isBreached });
    } catch {}
  });

  const eventsToday = (eventsByDate[calDate.toDateString()] || []).sort((a, b) => {
    try { return new Date(a.meeting.start) - new Date(b.meeting.start); } catch { return 0; }
  });

  return (
    <Card className={`border border-[#5B5FC7]/20 rounded-xl bg-white flex flex-col ${className}`} data-testid="all-cco-schedule-box">
      <CardHeader className="pb-0 pt-4 px-5">
        <CardTitle className="text-sm font-semibold text-[#5B5FC7] flex items-center gap-2">
          <CalendarIcon className="w-4 h-4" /> Schedule (All CCOs)
          <span className="ml-auto text-[10px] uppercase tracking-wider text-gray-400 font-medium">Upcoming {(items || []).length}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-5 pt-4 flex-1 min-h-0">
        <div className="flex flex-col xl:flex-row gap-5 h-full">
          {/* Calendar */}
          <div className="shrink-0 mx-auto xl:mx-0">
            <Calendar
              mode="single"
              selected={calDate}
              onSelect={(d) => d && setCalDate(d)}
              modifiers={{
                meeting: (date) => (eventsByDate[date.toDateString()] || []).some(e => e.event_type === "meeting"),
                followup: (date) => (eventsByDate[date.toDateString()] || []).some(e => (e.event_type === "followup" || e.event_type === "scheduled_followup") && !e.breached),
                overdue: (date) => (eventsByDate[date.toDateString()] || []).some(e => e.breached),
              }}
              modifiersClassNames={{
                meeting: "!bg-[#5B5FC7]/15 !text-[#5B5FC7] font-bold",
                followup: "!bg-emerald-100 !text-emerald-700 font-bold",
                overdue: "!bg-red-100 !text-red-700 font-bold",
              }}
              className="rounded-lg border border-gray-200"
            />
            <div className="flex items-center gap-4 mt-3 px-1 flex-wrap">
              <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-[#5B5FC7]" /><span className="text-[10px] text-gray-500">Demo Meeting</span></div>
              <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500" /><span className="text-[10px] text-gray-500">Follow-Up</span></div>
              <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-500" /><span className="text-[10px] text-gray-500">Overdue</span></div>
            </div>
          </div>

          {/* Events for selected date */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-gray-900">{format(calDate, "EEEE, d MMMM yyyy")}</h4>
              <span className="text-xs text-gray-400">{eventsToday.length} event{eventsToday.length !== 1 ? "s" : ""}</span>
            </div>
            {eventsToday.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-gray-300">
                <CalendarIcon className="w-8 h-8 mb-2" />
                <p className="text-sm text-gray-400">No events on this day</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
                {eventsToday.map((ev, i) => {
                  const isMeeting = ev.event_type === "meeting";
                  let timeStr = "";
                  if (isMeeting) {
                    try { timeStr = format(new Date(ev.meeting.start), "h:mm a"); } catch {}
                  }
                  const isBreached = !!ev.breached;
                  const tone = isBreached
                    ? "border-red-200 hover:bg-red-50 bg-red-50/40"
                    : isMeeting
                    ? "border-[#5B5FC7]/15 hover:bg-[#5B5FC7]/5"
                    : "border-emerald-200 hover:bg-emerald-50";
                  const iconBg = isBreached ? "bg-red-100" : isMeeting ? "bg-[#5B5FC7]/10" : "bg-emerald-100";
                  const subtone = isBreached ? "text-red-700" : isMeeting ? "text-[#5B5FC7]" : "text-emerald-700";
                  const isScheduledFu = ev.event_type === "scheduled_followup";
                  const dayLabel = ev.meeting?.day ? ` (+${ev.meeting.day}d)` : "";
                  const stageLabel = ev.meeting?.stage_status ? `${ev.meeting.stage_status}${dayLabel}` : `${ev.status || "—"}`;
                  const isRescheduled = !!ev.meeting?.rescheduled;
                  const note = ev.meeting?.note || ev.meeting?.notes;
                  // Look up assignee for avatar
                  const assignee = teamMembers?.find?.((m) => m.id === ev.assigned_to);
                  const initials = (ev.assigned_name || assignee?.name || "?").trim().charAt(0).toUpperCase();
                  return (
                    <button
                      key={i}
                      onClick={() => ev.lead_id && navigate(`/leads/${ev.lead_id}`)}
                      className={`w-full flex items-start gap-3 p-3 rounded-xl transition-colors text-left border ${tone}`}
                      data-testid={`cco-schedule-item-${ev.lead_id}-${i}`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
                        {isMeeting ? <Video className={`w-4 h-4 ${isBreached ? "text-red-600" : "text-[#5B5FC7]"}`} /> : <Clock className={`w-4 h-4 ${isBreached ? "text-red-600" : "text-emerald-600"}`} />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-medium text-gray-900 truncate">{ev.lead_name}{ev.company ? ` · ${ev.company}` : ""}</p>
                          {(assignee || ev.assigned_name) && (
                            <div className="inline-flex items-center gap-1.5 shrink-0">
                              {assignee?.avatar ? (
                                <img src={assignee.avatar} alt="" className="w-5 h-5 rounded-full object-cover" />
                              ) : (
                                <span className="w-5 h-5 rounded-full bg-[#0D0D5B] text-white inline-flex items-center justify-center text-[9px] font-semibold">{initials}</span>
                              )}
                              <span className="text-[11px] text-gray-500 font-medium">{ev.assigned_name || assignee?.name}</span>
                            </div>
                          )}
                        </div>
                        <div className={`text-xs ${subtone} flex items-center gap-1.5 flex-wrap mt-0.5`}>
                          <span>{isMeeting ? `Demo at ${timeStr}` : isScheduledFu ? `Follow-Up · ${stageLabel}` : `Follow-Up (${ev.status || "—"})`}</span>
                          {isBreached && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-800 border border-red-200" data-testid="schedule-breached-badge">
                              <AlertTriangle className="w-2.5 h-2.5" /> Overdue
                            </span>
                          )}
                          {isRescheduled && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200" data-testid="schedule-rescheduled-badge">
                              <RotateCcw className="w-2.5 h-2.5" /> Rescheduled{ev.meeting?.reschedule_count > 1 ? ` ${ev.meeting.reschedule_count}×` : ""}
                            </span>
                          )}
                        </div>
                        {note && (
                          <p className="text-[11px] text-gray-600 italic mt-1 line-clamp-2 break-words" title={note}>"{note}"</p>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-300 shrink-0 mt-1" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ==================== CCO MY DASHBOARD ====================
function CcoDashboardSection({ data, navigate, engarageSubscriptions = [] }) {
  const [calendarDate, setCalendarDate] = useState(new Date());
  if (!data) return null;
  const { new_leads, overdue_followups, contacted_leads, upcoming_meetings, all_followups = [], scheduled_followups = [], my_total, my_subscribed, my_conversion } = data;

  // Build a map of dates with events for the calendar
  const eventDates = {};
  const todayStr = new Date().toISOString().slice(0, 10);
  upcoming_meetings.forEach(m => {
    try {
      const d = new Date(m.meeting.start).toDateString();
      if (!eventDates[d]) eventDates[d] = [];
      eventDates[d].push({ type: "meeting", name: m.lead_name, time: format(new Date(m.meeting.start), "h:mm a"), lead_id: m.lead_id });
    } catch {}
  });
  all_followups.forEach(f => {
    try {
      const d = new Date(f.follow_up_date).toDateString();
      if (!eventDates[d]) eventDates[d] = [];
      eventDates[d].push({ type: "followup", name: f.name, status: f.status, lead_id: f.id });
    } catch {}
  });
  scheduled_followups.forEach(f => {
    try {
      const d = new Date(f.follow_up_date).toDateString();
      if (!eventDates[d]) eventDates[d] = [];
      eventDates[d].push({
        type: "scheduled_followup",
        name: f.name,
        status: f.status,
        day: f.day,
        stage: f.stage_status,
        lead_id: f.id,
        rescheduled: !!f.rescheduled,
        note: f.note,
        breached: (f.follow_up_date || "").slice(0, 10) < todayStr,
      });
    } catch {}
  });

  // Events for the selected date
  const selectedDateEvents = eventDates[calendarDate.toDateString()] || [];

  return (
    <div className="mb-6 space-y-4" data-testid="cco-my-dashboard">
      {/* My Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border border-[#0D0D5B]/20 rounded-xl bg-[#0D0D5B]/5">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0D0D5B] rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-white" /></div>
            <div><p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">My Leads</p><p className="text-2xl font-bold text-gray-900">{my_total}</p></div>
          </CardContent>
        </Card>
        <Card className="border border-emerald-200 rounded-xl bg-emerald-50/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center"><UserCheck className="w-5 h-5 text-white" /></div>
            <div><p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">My Subscribed</p><p className="text-2xl font-bold text-emerald-600">{my_subscribed}</p></div>
          </CardContent>
        </Card>
        <Card className="border border-purple-200 rounded-xl bg-purple-50/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center"><Target className="w-5 h-5 text-white" /></div>
            <div><p className="text-[10px] text-gray-500 uppercase tracking-wider font-medium">My Conversion</p><p className="text-2xl font-bold text-purple-600">{my_conversion}%</p></div>
          </CardContent>
        </Card>
      </div>

      {/* Action Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Overdue Follow-ups */}
        <Card className="border border-red-200 rounded-xl bg-white" data-testid="cco-overdue">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold text-red-700 flex items-center gap-2">
              <Clock className="w-4 h-4" /> Overdue Follow-ups ({overdue_followups.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {overdue_followups.length === 0 ? <p className="text-xs text-gray-400 text-center py-3">All caught up!</p> : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {overdue_followups.map(l => (
                  <button key={l.id} onClick={() => navigate(`/leads/${l.id}`)} className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-red-50 transition-colors text-left group" data-testid={`overdue-followup-${l.id}`}>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate">{l.name}</p>
                      <p className="text-[10px] text-red-500">
                        Due: {(() => { try { return format(new Date(l.follow_up_date), "d MMM"); } catch { return l.follow_up_date; } })()}{l.day ? ` · +${l.day}d` : ""}
                      </p>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-red-500 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* New Leads to Contact */}
        <Card className="border border-blue-200 rounded-xl bg-white" data-testid="cco-new-leads">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold text-blue-700 flex items-center gap-2">
              <UserPlus className="w-4 h-4" /> New Leads to Contact ({new_leads.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {new_leads.length === 0 ? <p className="text-xs text-gray-400 text-center py-3">No new leads</p> : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {new_leads.map(l => (
                  <button key={l.id} onClick={() => navigate(`/leads/${l.id}`)} className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-blue-50 transition-colors text-left group">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate">{l.name}</p>
                      {l.company && <p className="text-[10px] text-gray-400 truncate">{l.company}</p>}
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-blue-500 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contacted — Need Follow-up */}
        <Card className="border border-amber-200 rounded-xl bg-white" data-testid="cco-contacted">
          <CardHeader className="pb-2 pt-4 px-4">
            <CardTitle className="text-sm font-semibold text-amber-700 flex items-center gap-2">
              <Phone className="w-4 h-4" /> Contacted — Schedule Demo ({contacted_leads.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            {contacted_leads.length === 0 ? <p className="text-xs text-gray-400 text-center py-3">No contacted leads</p> : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {contacted_leads.map(l => (
                  <button key={l.id} onClick={() => navigate(`/leads/${l.id}`)} className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-amber-50 transition-colors text-left group">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate">{l.name}</p>
                      {l.company && <p className="text-[10px] text-gray-400 truncate">{l.company}</p>}
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-amber-500 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* My Schedule + ENGARAGE Subscriptions — 2/3 + 1/3 grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
      <Card className="border border-[#5B5FC7]/20 rounded-xl bg-white flex flex-col lg:col-span-2" data-testid="cco-schedule">
        <CardHeader className="pb-0 pt-4 px-5">
          <CardTitle className="text-sm font-semibold text-[#5B5FC7] flex items-center gap-2">
            <CalendarIcon className="w-4 h-4" /> My Schedule
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 pb-5 pt-4 flex-1 min-h-0">
          <div className="flex flex-col xl:flex-row gap-5">
            {/* Calendar */}
            <div className="shrink-0 mx-auto xl:mx-0">
              <Calendar
                mode="single"
                selected={calendarDate}
                onSelect={(d) => d && setCalendarDate(d)}
                modifiers={{
                  meeting: (date) => (eventDates[date.toDateString()] || []).some(e => e.type === "meeting"),
                  followup: (date) => (eventDates[date.toDateString()] || []).some(e => (e.type === "followup" || e.type === "scheduled_followup") && !e.breached),
                  overdue: (date) => (eventDates[date.toDateString()] || []).some(e => e.breached),
                }}
                modifiersClassNames={{
                  meeting: "!bg-[#5B5FC7]/15 !text-[#5B5FC7] font-bold",
                  followup: "!bg-emerald-100 !text-emerald-700 font-bold",
                  overdue: "!bg-red-100 !text-red-700 font-bold",
                }}
                className="rounded-lg border border-gray-200"
              />
              {/* Legend */}
              <div className="flex items-center gap-4 mt-3 px-1 flex-wrap">
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-[#5B5FC7]" /><span className="text-[10px] text-gray-500">Demo Meeting</span></div>
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500" /><span className="text-[10px] text-gray-500">Follow-Up</span></div>
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-500" /><span className="text-[10px] text-gray-500">Overdue</span></div>
              </div>
            </div>

            {/* Events Panel */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-900">{format(calendarDate, "EEEE, d MMMM yyyy")}</h4>
                <span className="text-xs text-gray-400">{selectedDateEvents.length} event{selectedDateEvents.length !== 1 ? "s" : ""}</span>
              </div>
              {selectedDateEvents.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-gray-300">
                  <CalendarIcon className="w-8 h-8 mb-2" />
                  <p className="text-sm text-gray-400">No events on this day</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
                  {selectedDateEvents.map((ev, i) => {
                    const isMeeting = ev.type === "meeting";
                    const isScheduledFu = ev.type === "scheduled_followup";
                    const isBreached = !!ev.breached;
                    const tone = isBreached
                      ? "border-red-200 hover:bg-red-50 bg-red-50/40"
                      : isMeeting
                      ? "border-[#5B5FC7]/15 hover:bg-[#5B5FC7]/5"
                      : "border-emerald-200 hover:bg-emerald-50";
                    const iconBg = isBreached ? "bg-red-100" : isMeeting ? "bg-[#5B5FC7]/10" : "bg-emerald-100";
                    const subtone = isBreached ? "text-red-700" : isMeeting ? "text-[#5B5FC7]" : "text-emerald-700";
                    return (
                      <button key={i} onClick={() => navigate(`/leads/${ev.lead_id}`)} className={`w-full flex items-start gap-3 p-3 rounded-xl transition-colors text-left group border ${tone}`} data-testid={`my-schedule-item-${ev.lead_id}-${i}`}>
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
                          {isMeeting ? <Video className={`w-4 h-4 ${isBreached ? "text-red-600" : "text-[#5B5FC7]"}`} /> : <Clock className={`w-4 h-4 ${isBreached ? "text-red-600" : "text-emerald-600"}`} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{ev.name}</p>
                          <div className={`text-xs ${subtone} flex items-center gap-1.5 flex-wrap mt-0.5`}>
                            <span>{isMeeting ? `Demo at ${ev.time}` : isScheduledFu ? `Follow-Up · ${ev.stage} (+${ev.day}d)` : `Follow-Up (${ev.status})`}</span>
                            {isBreached && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-800 border border-red-200">
                                <AlertTriangle className="w-2.5 h-2.5" /> Overdue
                              </span>
                            )}
                            {ev.rescheduled && (
                              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                                <RotateCcw className="w-2.5 h-2.5" /> Rescheduled
                              </span>
                            )}
                          </div>
                          {ev.note && (
                            <p className="text-[11px] text-gray-600 italic mt-1 line-clamp-2 break-words" title={ev.note}>"{ev.note}"</p>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 shrink-0 mt-1" />
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Upcoming meetings summary */}
              {upcoming_meetings.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-2">Upcoming Meetings</p>
                  <div className="flex flex-wrap gap-2">
                    {upcoming_meetings.slice(0, 5).map((m, i) => (
                      <button key={i} onClick={() => navigate(`/leads/${m.lead_id}`)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[#5B5FC7]/5 border border-[#5B5FC7]/15 rounded-lg hover:bg-[#5B5FC7]/10 transition-colors">
                        <Video className="w-3 h-3 text-[#5B5FC7]" />
                        <span className="text-[11px] font-medium text-gray-800 truncate max-w-[100px]">{m.lead_name}</span>
                        <span className="text-[10px] text-[#5B5FC7]">{(() => { try { return format(new Date(m.meeting.start), "d MMM"); } catch { return ""; } })()}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      <EngarageSubscriptionsBox items={engarageSubscriptions} navigate={navigate} />
      </div>
    </div>
  );
}

// ==================== MAIN DASHBOARD ====================
const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [dashData, setDashData] = useState(null);
  const [filteredAnalytics, setFilteredAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [activePreset, setActivePreset] = useState("all");

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [baseRes, dashRes] = await Promise.all([
        axios.get(`${API}/analytics`),
        axios.get(`${API}/analytics/dashboard`),
      ]);
      setAnalytics(baseRes.data);
      setDashData(dashRes.data);
      setFilteredAnalytics(null);
    } catch { toast.error("Failed to fetch analytics"); }
    finally { setLoading(false); }
  };

  const fetchFilteredAnalytics = async (from, to) => {
    try {
      const params = new URLSearchParams();
      if (from) params.set("date_from", format(from, "yyyy-MM-dd"));
      if (to) params.set("date_to", format(to, "yyyy-MM-dd"));
      const { data } = await axios.get(`${API}/analytics/filtered?${params}`);
      setFilteredAnalytics(data);
    } catch { toast.error("Failed to fetch filtered analytics"); }
  };

  const applyDatePreset = (preset) => {
    setActivePreset(preset);
    const today = new Date();
    let from = null, to = today;
    if (preset === "7d") from = subDays(today, 7);
    else if (preset === "30d") from = subDays(today, 30);
    else if (preset === "90d") from = subDays(today, 90);
    else if (preset === "6m") from = subMonths(today, 6);
    else { setDateFrom(null); setDateTo(null); setFilteredAnalytics(null); return; }
    setDateFrom(from); setDateTo(to);
    fetchFilteredAnalytics(from, to);
  };

  const applyCustomRange = () => {
    if (dateFrom && dateTo) { setActivePreset("custom"); fetchFilteredAnalytics(dateFrom, dateTo); }
  };

  const seedData = async () => {
    try { setSeeding(true); await axios.post(`${API}/seed`); toast.success("Successfully seeded 500 leads!"); fetchAnalytics(); }
    catch { toast.error("Failed to seed data"); } finally { setSeeding(false); }
  };

  const exportCSV = async () => {
    try {
      const response = await axios.get(`${API}/leads/export/csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'leads_export.csv');
      document.body.appendChild(link); link.click(); link.remove();
      toast.success("Leads exported successfully!");
    } catch { toast.error("Failed to export leads"); }
  };

  useEffect(() => { fetchAnalytics(); }, []);

  const displayData = filteredAnalytics || analytics;
  const statusData = displayData ? Object.entries(displayData.by_status).map(([name, value]) => ({ name, value, fill: statusColors[name] })).filter(d => d.value > 0) : [];

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-[1600px] mx-auto animate-fade-in">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="border border-gray-200 rounded-xl bg-white"><CardContent className="p-4"><div className="h-16 bg-gray-100 rounded-lg animate-pulse" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  const ccoData = dashData?.cco_performance || [];
  const funnelData = dashData?.funnel || [];
  const monthlyTrend = dashData?.monthly_trend || [];
  const planBreakdown = dashData?.plan_breakdown || {};
  const planData = Object.entries(planBreakdown).filter(([,v]) => v > 0).map(([name, value]) => ({ name, value, fill: planColors[name] || "#6B7280" }));
  const totalSubscribed = planData.reduce((sum, d) => sum + d.value, 0);
  const isCco = user?.role === "cco";
  const hasMyDashboard = !!dashData?.cco_my_dashboard;

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{hasMyDashboard ? `Welcome, ${user.name}` : "Dashboard"}</h1>
          <p className="text-sm text-gray-500 mt-1">{hasMyDashboard ? "Your leads & next actions" : "Sales pipeline & CCO performance overview"}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchAnalytics} className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV} className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          {analytics?.total_leads === 0 && !filteredAnalytics && (
            <Button size="sm" onClick={seedData} disabled={seeding} className="bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg" data-testid="seed-data-btn">
              {seeding ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />} Seed 500 Leads
            </Button>
          )}
        </div>
      </div>

      {/* Personal Dashboard (for any assignable user) */}
      {hasMyDashboard && (
        <CcoDashboardSection
          data={dashData.cco_my_dashboard}
          navigate={navigate}
          engarageSubscriptions={dashData?.engarage_subscriptions || []}
        />
      )}

      {/* Admin/Marketing top section: All-CCO Schedule + ENGARAGE Subscriptions (2/3 + 1/3) */}
      {!hasMyDashboard && (dashData?.all_cco_schedule !== undefined) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch mb-6" data-testid="admin-overview-row">
          <AllCcoScheduleBox items={dashData.all_cco_schedule || []} navigate={navigate} teamMembers={dashData.cco_performance || []} className="lg:col-span-2" />
          <EngarageSubscriptionsBox items={dashData?.engarage_subscriptions || []} navigate={navigate} />
        </div>
      )}

      {/* Date Range Filter */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="date-range-filter">
        {[{ key: "all", label: "All Time" }, { key: "7d", label: "7 Days" }, { key: "30d", label: "30 Days" }, { key: "90d", label: "90 Days" }, { key: "6m", label: "6 Months" }].map(p => (
          <button key={p.key} onClick={() => applyDatePreset(p.key)} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${activePreset === p.key ? "bg-[#0D0D5B] text-white" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`} data-testid={`date-preset-${p.key}`}>
            {p.label}
          </button>
        ))}
        <Popover>
          <PopoverTrigger asChild>
            <button className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors flex items-center gap-1 ml-2 ${activePreset === "custom" ? "bg-[#0D0D5B] text-white border-[#0D0D5B]" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`} data-testid="custom-date-btn">
              <CalendarIcon className="w-3 h-3" />
              {dateFrom && dateTo && activePreset === "custom" ? `${format(dateFrom, "d MMM")} - ${format(dateTo, "d MMM")}` : "Custom Range"}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="start">
            <div className="space-y-3">
              <div><p className="text-xs font-medium text-gray-500 mb-1">From</p><Calendar mode="single" selected={dateFrom} onSelect={setDateFrom} initialFocus /></div>
              <div><p className="text-xs font-medium text-gray-500 mb-1">To</p><Calendar mode="single" selected={dateTo} onSelect={setDateTo} /></div>
              <Button size="sm" onClick={applyCustomRange} disabled={!dateFrom || !dateTo} className="w-full bg-[#0D0D5B] hover:bg-[#1a1a7a] text-white rounded-lg" data-testid="apply-custom-date">Apply Range</Button>
            </div>
          </PopoverContent>
        </Popover>
        {filteredAnalytics && <span className="text-xs text-gray-500 ml-2">Filtered: {displayData.total_leads} leads</span>}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {[
          { label: "Total Leads", value: displayData?.total_leads, icon: Users, iconBg: "bg-[#0D0D5B]", color: "text-gray-900", testid: "total-leads-card" },
          { label: "New", value: displayData?.by_status?.New, icon: UserPlus, iconBg: "bg-blue-50 !text-blue-600", iconColor: "text-blue-600", color: "text-gray-900", testid: "new-leads-card" },
          { label: "Subscribed", value: displayData?.by_status?.Subscribed, icon: UserCheck, iconBg: "bg-emerald-50", iconColor: "text-emerald-600", color: "text-emerald-600", testid: "converted-leads-card" },
          { label: "Conversion", value: `${dashData?.conversion_rate || 0}%`, icon: TrendingUp, iconBg: "bg-purple-50", iconColor: "text-purple-600", color: "text-gray-900", testid: "conversion-rate-card", raw: true },
          { label: "Demo \u2192 Sub", value: `${dashData?.demo_to_sub_rate || 0}%`, icon: Target, iconBg: "bg-amber-50", iconColor: "text-amber-600", color: "text-gray-900", testid: "demo-to-sub-card", raw: true },
          { label: "Loss Rate", value: `${dashData?.loss_rate || 0}%`, icon: TrendingDown, iconBg: "bg-red-50", iconColor: "text-red-500", color: "text-red-500", testid: "loss-rate-card", raw: true },
        ].map(kpi => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.testid} className="border border-gray-200 rounded-xl bg-white" data-testid={kpi.testid}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${kpi.iconBg}`}>
                    <Icon className={`w-5 h-5 ${kpi.iconColor || "text-white"}`} />
                  </div>
                  <div>
                    <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">{kpi.label}</p>
                    <p className={`text-2xl font-bold ${kpi.color}`}>{kpi.raw ? kpi.value : (kpi.value?.toLocaleString() || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Alert Cards */}
      {!hasMyDashboard && (dashData?.unassigned_leads > 0 || dashData?.overdue_followups > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {dashData?.unassigned_leads > 0 && (
            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="unassigned-alert">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <div><p className="text-sm font-medium text-amber-800">{dashData.unassigned_leads} Unassigned Leads</p><p className="text-xs text-amber-600">Assign to a CCO to start follow-up</p></div>
            </div>
          )}
          {dashData?.overdue_followups > 0 && (
            <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="overdue-alert">
              <Clock className="w-5 h-5 text-red-500 shrink-0" />
              <div><p className="text-sm font-medium text-red-800">{dashData.overdue_followups} Overdue Follow-ups</p><p className="text-xs text-red-600">Leads with past follow-up dates that need attention</p></div>
            </div>
          )}
        </div>
      )}

      {/* Row: Funnel + Subscription Plans + Status Distribution (3 inline) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Sales Funnel */}
        <Card className="border border-gray-200 rounded-xl bg-white" data-testid="sales-funnel">
          <CardHeader className="pb-0"><CardTitle className="text-base font-semibold text-gray-900">Sales Funnel</CardTitle></CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              {funnelData.map((stage, i) => {
                const maxCount = Math.max(...funnelData.map(f => f.count), 1);
                const pct = Math.round((stage.count / maxCount) * 100);
                const convPct = funnelData[0]?.count > 0 ? Math.round((stage.count / funnelData[0].count) * 100) : 0;
                return (
                  <div key={stage.stage} data-testid={`funnel-${stage.stage.toLowerCase()}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-gray-700">{stage.stage}</span>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-gray-900">{stage.count.toLocaleString()}</span>
                        <span className="text-[10px] text-gray-400">{convPct}%</span>
                      </div>
                    </div>
                    <div className="h-6 bg-gray-100 rounded-lg overflow-hidden">
                      <div className="h-full rounded-lg transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: funnelColors[i] || "#6B7280" }} />
                    </div>
                  </div>
                );
              })}
              {funnelData.length >= 4 && (
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <span className="text-[11px] text-gray-500">Overall Conversion</span>
                  <span className="text-xs font-bold text-emerald-600">{funnelData[0]?.count > 0 ? Math.round((funnelData[3]?.count / funnelData[0]?.count) * 100) : 0}%</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Subscription Plan Breakdown */}
        <Card className="border border-gray-200 rounded-xl bg-white" data-testid="plan-breakdown">
          <CardHeader className="pb-0"><CardTitle className="text-base font-semibold text-gray-900">Subscription Plans</CardTitle></CardHeader>
          <CardContent className="pt-4">
            <div className="flex flex-col items-center gap-3">
              <div className="w-[150px] h-[150px] shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={planData} cx="50%" cy="50%" innerRadius={42} outerRadius={68} paddingAngle={3} dataKey="value">
                      {planData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-full space-y-2">
                {planData.map(p => (
                  <div key={p.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.fill }} />
                      <span className="text-xs font-medium text-gray-700">{p.name}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-gray-900">{p.value}</span>
                      <span className="text-[10px] text-gray-400">{totalSubscribed > 0 ? Math.round((p.value / totalSubscribed) * 100) : 0}%</span>
                    </div>
                  </div>
                ))}
                <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-[11px] text-gray-500">Total Subscribed</span>
                  <span className="text-xs font-bold text-emerald-600">{totalSubscribed}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lead Status Distribution */}
        <Card className="border border-gray-200 rounded-xl bg-white" data-testid="leads-by-status-chart">
          <CardHeader className="pb-0"><CardTitle className="text-base font-semibold text-gray-900">Lead Status Distribution</CardTitle></CardHeader>
          <CardContent className="pt-4">
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {statusData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CCO Performance Ranking */}
      <Card className="border border-gray-200 rounded-xl bg-white mb-6" data-testid="cco-performance">
        <CardHeader className="pb-0">
          <CardTitle className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" /> CCO Performance Ranking
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {ccoData.length === 0 ? <p className="text-sm text-gray-400 text-center py-6">No CCO data available</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cco-table">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="py-2.5 px-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-left">#</th>
                    <th className="py-2.5 px-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-left">CCO</th>
                    <th className="py-2.5 px-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-center">Assigned</th>
                    <th className="py-2.5 px-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-center">New</th>
                    <th className="py-2.5 px-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-center">Contacted</th>
                    <th className="py-2.5 px-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-center">Demoed</th>
                    <th className="py-2.5 px-2 text-[10px] font-semibold text-emerald-600 uppercase tracking-wider text-center border-l border-gray-100">Total</th>
                    <th className="py-2.5 px-2 text-[10px] font-semibold text-blue-600 uppercase tracking-wider text-center">Basic</th>
                    <th className="py-2.5 px-2 text-[10px] font-semibold text-purple-600 uppercase tracking-wider text-center">Plus</th>
                    <th className="py-2.5 px-2 text-[10px] font-semibold text-amber-700 uppercase tracking-wider text-center border-r border-gray-100">Pro</th>
                    <th className="py-2.5 px-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-center">Lost</th>
                    <th className="py-2.5 px-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-center">Meetings</th>
                    <th className="py-2.5 px-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-center">Conv.</th>
                    <th className="py-2.5 px-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider text-center">Demo Rate</th>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <th colSpan={6}></th>
                    <th colSpan={4} className="py-1 text-[9px] font-semibold text-emerald-600 uppercase tracking-widest text-center bg-emerald-50/50 border-l border-r border-gray-100">Subscribed</th>
                    <th colSpan={4}></th>
                  </tr>
                </thead>
                <tbody>
                  {ccoData.map((cco, i) => (
                    <tr key={cco.id} className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${i === 0 ? "bg-amber-50/30" : ""}`} data-testid={`cco-row-${cco.id}`}>
                      <td className="py-3 px-3">{i === 0 ? <span className="text-amber-500 font-bold text-lg">1</span> : <span className="text-gray-400 font-medium">{i + 1}</span>}</td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          {cco.avatar ? (
                            <img src={cco.avatar} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                          ) : (
                            <span className="w-8 h-8 rounded-full bg-[#0D0D5B] text-white inline-flex items-center justify-center text-xs font-semibold shrink-0">
                              {(cco.name || "?").trim().charAt(0).toUpperCase()}
                            </span>
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{cco.name}</p>
                            <p className="text-[11px] text-gray-400 truncate">{cco.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3 text-center font-semibold text-gray-900">{cco.total_assigned}</td>
                      <td className="py-3 px-3 text-center text-[#0D0D5B] font-medium">{cco.new}</td>
                      <td className="py-3 px-3 text-center text-blue-600 font-medium">{cco.contacted}</td>
                      <td className="py-3 px-3 text-center text-purple-600 font-medium">{cco.demoed}</td>
                      {/* Subscribed breakdown */}
                      <td className="py-3 px-2 text-center border-l border-gray-100">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">{cco.subscribed}</span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        {cco.sub_basic > 0 ? <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-blue-100 text-blue-700">{cco.sub_basic}</span> : <span className="text-gray-200">—</span>}
                      </td>
                      <td className="py-3 px-2 text-center">
                        {cco.sub_plus > 0 ? <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-purple-100 text-purple-700">{cco.sub_plus}</span> : <span className="text-gray-200">—</span>}
                      </td>
                      <td className="py-3 px-2 text-center border-r border-gray-100">
                        {cco.sub_pro > 0 ? <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-amber-100 text-amber-800">{cco.sub_pro}</span> : <span className="text-gray-200">—</span>}
                      </td>
                      <td className="py-3 px-3 text-center text-red-400 font-medium">{cco.lost}</td>
                      <td className="py-3 px-3 text-center"><span className="inline-flex items-center gap-0.5 text-xs text-gray-600"><Video className="w-3 h-3" /> {cco.meetings}</span></td>
                      <td className="py-3 px-3 text-center"><span className={`text-xs font-bold ${cco.conversion_rate >= 20 ? "text-emerald-600" : cco.conversion_rate >= 10 ? "text-amber-600" : "text-red-500"}`}>{cco.conversion_rate}%</span></td>
                      <td className="py-3 px-3 text-center text-xs font-medium text-purple-600">{cco.demo_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* CCO Bar Chart + Monthly/Daily Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="border border-gray-200 rounded-xl bg-white" data-testid="cco-bar-chart">
          <CardHeader className="pb-0"><CardTitle className="text-base font-semibold text-gray-900">CCO Subscriptions Comparison</CardTitle></CardHeader>
          <CardContent className="pt-4">
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ccoData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={true} vertical={false} />
                  <XAxis type="number" stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis dataKey="name" type="category" stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} width={75} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="subscribed" fill="#10B981" name="Subscribed" radius={[0, 4, 4, 0]} barSize={14} />
                  <Bar dataKey="demoed" fill="#8B5CF6" name="Demoed" radius={[0, 4, 4, 0]} barSize={14} />
                  <Bar dataKey="lost" fill="#EF4444" name="Lost" radius={[0, 4, 4, 0]} barSize={14} />
                  <Legend verticalAlign="top" height={30} iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 rounded-xl bg-white" data-testid="monthly-trend-chart">
          <CardHeader className="pb-0"><CardTitle className="text-base font-semibold text-gray-900">Monthly Trend (6 Months)</CardTitle></CardHeader>
          <CardContent className="pt-4">
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthlyTrend}>
                  <defs><linearGradient id="colorMonthly" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0D0D5B" stopOpacity={0.1} /><stop offset="95%" stopColor="#0D0D5B" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="month" stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend verticalAlign="top" height={30} iconSize={8} wrapperStyle={{ fontSize: '11px' }} />
                  <Area type="monotone" dataKey="leads" fill="url(#colorMonthly)" stroke="#0D0D5B" strokeWidth={2} name="Leads" dot={{ fill: '#0D0D5B', r: 3 }} />
                  <Bar dataKey="subscribed" fill="#10B981" name="Subscribed" radius={[4, 4, 0, 0]} barSize={20} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lost Reasons Donut + Daily Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Lost-Reason Donut (1/3 width) */}
        <Card className="border border-gray-200 rounded-xl bg-white" data-testid="lost-reasons-chart">
          <CardHeader className="pb-0">
            <CardTitle className="text-base font-semibold text-gray-900">Lost Reasons</CardTitle>
            {dashData?.lost_not_actual_workshop_count > 0 && (
              <p className="text-[11px] text-amber-700 mt-0.5">
                {dashData.lost_not_actual_workshop_count} flagged "not actual workshop"
              </p>
            )}
          </CardHeader>
          <CardContent className="pt-4">
            <div className="h-[260px]">
              {(dashData?.lost_reasons || []).length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={(dashData.lost_reasons || []).map((r, i) => ({ name: r.reason, value: r.count, fill: LOST_REASON_COLORS[i % LOST_REASON_COLORS.length] }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={42}
                      outerRadius={68}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {(dashData.lost_reasons || []).map((r, i) => (
                        <Cell key={i} fill={LOST_REASON_COLORS[i % LOST_REASON_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} formatter={(value, name) => [`${value} lead${value === 1 ? "" : "s"}`, name]} />
                    <Legend verticalAlign="bottom" height={36} iconSize={8} wrapperStyle={{ fontSize: '10px', maxHeight: '60px', overflowY: 'auto' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-center px-4">
                  <div>
                    <Ban className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 font-medium">No lost leads yet</p>
                    <p className="text-[11px] text-gray-400 mt-1">Reasons will appear here as leads are marked Lost.</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Daily Trend (2/3 width) */}
        <Card className="border border-gray-200 rounded-xl bg-white lg:col-span-2" data-testid="trends-chart">
          <CardHeader className="pb-0"><CardTitle className="text-base font-semibold text-gray-900">Daily Trend (Last 7 Days)</CardTitle></CardHeader>
          <CardContent className="pt-4">
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics?.recent_trends || []}>
                  <defs><linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0D0D5B" stopOpacity={0.15} /><stop offset="95%" stopColor="#0D0D5B" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                  <XAxis dataKey="date" stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#9CA3AF" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area type="monotone" dataKey="leads" stroke="#0D0D5B" strokeWidth={2} fill="url(#colorLeads)" dot={{ fill: '#0D0D5B', stroke: '#fff', strokeWidth: 2, r: 4 }} activeDot={{ r: 6, fill: '#0D0D5B', stroke: '#fff', strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
